import * as turf from '@turf/turf';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';

import type { AoiLandCoverCategoryKey, AoiLandCoverImpact } from '@/types/flood.types';

const DEFAULT_KEYS: AoiLandCoverCategoryKey[] = [
  'ricePaddy',
  'urbanBuilt',
  'forest',
  'perennialCrop',
  'surfaceWater',
];

/** Màu minh họa (UI) — khớp legend tab Lớp phủ. */
export const MOCK_LAND_COVER_FILL: Record<AoiLandCoverCategoryKey, string> = {
  ricePaddy: 'rgba(187, 222, 140, 0.72)',
  urbanBuilt: 'rgba(148, 163, 184, 0.75)',
  forest: 'rgba(34, 139, 34, 0.72)',
  perennialCrop: 'rgba(180, 160, 90, 0.72)',
  surfaceWater: 'rgba(56, 189, 248, 0.78)',
};

export const MOCK_LAND_COVER_STROKE: Record<AoiLandCoverCategoryKey, string> = {
  ricePaddy: 'rgba(74, 124, 42, 0.95)',
  urbanBuilt: 'rgba(71, 85, 105, 0.95)',
  forest: 'rgba(20, 83, 45, 0.95)',
  perennialCrop: 'rgba(120, 90, 40, 0.95)',
  surfaceWater: 'rgba(14, 116, 144, 0.95)',
};

/** Màu ô legend (solid) */
export const MOCK_LAND_COVER_LEGEND_SWATCH: Record<AoiLandCoverCategoryKey, string> = {
  ricePaddy: '#9ccc7a',
  urbanBuilt: '#94a3b8',
  forest: '#228b22',
  perennialCrop: '#b4a05a',
  surfaceWater: '#38bdf8',
};

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface LandCoverDistributionRow {
  categoryKey: AoiLandCoverCategoryKey;
  areaHa: number;
  /** 0–100, hoặc `null` khi không có tổng diện tích để tính tỷ lệ. */
  pctOfTotal: number | null;
}

/**
 * Gộp `areaHa` theo `categoryKey` và tính phần trăm trên tổng diện tích các lớp (5 lớp cố định).
 *
 * @param impacts — Các dòng từ tab Tác động / AOI context (có thể trùng `categoryKey`, sẽ cộng dồn).
 * @returns Một dòng cho mỗi lớp trong thứ tự legend; `pctOfTotal` là `null` khi tổng ha = 0.
 */
export function computeLandCoverDistribution(
  impacts: AoiLandCoverImpact[],
): LandCoverDistributionRow[] {
  const byKey = new Map<AoiLandCoverCategoryKey, number>();
  for (const k of DEFAULT_KEYS) byKey.set(k, 0);
  for (const row of impacts) {
    const prev = byKey.get(row.categoryKey) ?? 0;
    byKey.set(row.categoryKey, prev + Math.max(0, row.areaHa));
  }
  const totalHa = [...byKey.values()].reduce((a, b) => a + b, 0);
  return DEFAULT_KEYS.map((categoryKey) => {
    const areaHa = Math.round((byKey.get(categoryKey) ?? 0) * 10) / 10;
    const pctOfTotal =
      totalHa > 0 ? Math.round(((100 * (byKey.get(categoryKey) ?? 0)) / totalHa) * 10) / 10 : null;
    return { categoryKey, areaHa, pctOfTotal };
  });
}

function normalizeImpacts(impacts: AoiLandCoverImpact[]): {
  keys: AoiLandCoverCategoryKey[];
  weights: number[];
} {
  if (!impacts.length) {
    const w = 1 / DEFAULT_KEYS.length;
    return { keys: [...DEFAULT_KEYS], weights: DEFAULT_KEYS.map(() => w) };
  }
  const byKey = new Map<AoiLandCoverCategoryKey, number>();
  for (const k of DEFAULT_KEYS) byKey.set(k, 0);
  for (const row of impacts) {
    const prev = byKey.get(row.categoryKey) ?? 0;
    byKey.set(row.categoryKey, prev + Math.max(0, row.areaHa));
  }
  const keys = DEFAULT_KEYS.filter((k) => (byKey.get(k) ?? 0) > 0);
  const raw = keys.map((k) => byKey.get(k) ?? 0);
  const sum = raw.reduce((a, b) => a + b, 0) || 1;
  const weights = raw.map((x) => x / sum);
  if (keys.length === 0) {
    const w = 1 / DEFAULT_KEYS.length;
    return { keys: [...DEFAULT_KEYS], weights: DEFAULT_KEYS.map(() => w) };
  }
  return { keys, weights };
}

function allocateCounts(n: number, weights: number[]): number[] {
  const raw = weights.map((w) => Math.floor(w * n));
  let diff = n - raw.reduce((a, b) => a + b, 0);
  let i = 0;
  while (diff > 0) {
    raw[i % raw.length] += 1;
    diff -= 1;
    i += 1;
  }
  return raw.map((c) => Math.max(0, c));
}

/**
 * Sinh GeoJSON polygon lưới trong AOI, gán `categoryKey` theo tỷ lệ `impacts` (demo UI).
 * Không dùng cho phân tích thực — thay bằng API/GEE khi có dữ liệu chính thức.
 */
export function buildMockLandCoverGeoJson(
  aoi: Polygon,
  impacts: AoiLandCoverImpact[],
  dateSeed: string,
): FeatureCollection {
  const aoiFeature: Feature<Polygon> = { type: 'Feature', properties: {}, geometry: aoi };
  const bbox = turf.bbox(aoiFeature);
  const { keys, weights } = normalizeImpacts(impacts);

  const bboxPoly = turf.bboxPolygon(bbox);
  const diagonalKm = turf.distance(
    turf.point(bboxPoly.geometry.coordinates[0][0]),
    turf.point(bboxPoly.geometry.coordinates[0][2]),
    { units: 'kilometers' },
  );

  const rng = mulberry32(hashSeed(`${dateSeed}:${JSON.stringify(bbox)}`));

  let cellKm = Math.max(0.025, Math.min(diagonalKm / 14, diagonalKm / 5));
  let grid = turf.squareGrid(bbox, cellKm, {
    units: 'kilometers',
    mask: aoiFeature as Feature<Polygon>,
  });

  const MAX_CELLS = 200;
  let guard = 0;
  while (grid.features.length > MAX_CELLS && guard < 16) {
    cellKm *= 1.35;
    grid = turf.squareGrid(bbox, cellKm, {
      units: 'kilometers',
      mask: aoiFeature as Feature<Polygon>,
    });
    guard += 1;
  }
  let shrink = 0;
  while (grid.features.length < 10 && shrink < 28) {
    cellKm *= 0.72;
    grid = turf.squareGrid(bbox, cellKm, {
      units: 'kilometers',
      mask: aoiFeature as Feature<Polygon>,
    });
    shrink += 1;
  }

  let cells = [...grid.features];
  if (cells.length > MAX_CELLS) {
    cells = cells.slice(0, MAX_CELLS);
  }
  for (let i = cells.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }

  const n = cells.length;
  const counts = allocateCounts(n, weights);

  const out: Feature<Polygon | MultiPolygon>[] = [];
  let cellIdx = 0;
  for (let c = 0; c < keys.length; c += 1) {
    const key = keys[c];
    const take = counts[c] ?? 0;
    for (let t = 0; t < take && cellIdx < cells.length; t += 1, cellIdx += 1) {
      const cell = cells[cellIdx];
      if (cell.geometry.type !== 'Polygon') continue;
      try {
        const inter = turf.intersect(
          turf.featureCollection([cell as Feature<Polygon>, aoiFeature]),
        );
        if (!inter?.geometry) continue;
        const geom = inter.geometry;
        if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') continue;
        out.push({
          type: 'Feature',
          properties: { categoryKey: key },
          geometry: geom,
        });
      } catch {
        /* skip */
      }
    }
  }

  return {
    type: 'FeatureCollection',
    features: out,
  };
}
